import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  formatTeacherValue,
  getTeacherDisplayName,
  normalizeAvailabilityCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  TeacherBanner,
  TeacherCard,
  TeacherEmptyState,
  TeacherKeyValueGrid,
  TeacherLoadingState,
  TeacherPageShell,
  buildTeacherEntries,
} from "./TeacherUI";

const prioritizedTutorKeys = [
  "id",
  "uuid",
  "name",
  "full_name",
  "email",
  "phone",
  "year",
  "experience",
  "rating",
];

const renderAvailabilityLabel = (slot) => {
  if (typeof slot === "string") return slot;

  if (slot?.label) return slot.label;

  const pieces = [
    slot?.date,
    slot?.start_time && slot?.end_time
      ? `${slot.start_time} - ${slot.end_time}`
      : slot?.start_time || slot?.end_time,
    slot?.slot_duration ? `${slot.slot_duration} mins` : "",
    slot?.day_of_week,
  ].filter(Boolean);

  return pieces.length ? pieces.join(" | ") : formatTeacherValue(slot);
};

export default function TutorDetail() {
  const { tutorId } = useParams();

  const tutorDetailQuery = useQuery({
    queryKey: ["tutor-detail", tutorId],
    queryFn: () => teacherApi.getTutorDetail(tutorId),
    enabled: Boolean(tutorId),
    retry: false,
  });

  const tutorAvailabilityQuery = useQuery({
    queryKey: ["tutor-availability", tutorId],
    queryFn: () => teacherApi.getTutorAvailability(tutorId),
    enabled: Boolean(tutorId) && !tutorDetailQuery.isError,
    retry: false,
  });

  const tutor = tutorDetailQuery.data || {};
  const availability = useMemo(
    () => normalizeAvailabilityCollection(tutorAvailabilityQuery.data),
    [tutorAvailabilityQuery.data]
  );
  const tutorEntries = buildTeacherEntries(tutor, prioritizedTutorKeys);
  const tutorNotFound = tutorDetailQuery.error?.status === 404;

  return (
    <TeacherPageShell
      pageTitle="Tutor Detail"
      pageSubtitle="Public tutor detail and availability powered by the tutor endpoints."
      email="Public access"
      headerStatus="Public page"
      tabs={[]}
    >
      <TeacherBanner
        title={tutorNotFound ? "Tutor Not Found" : getTeacherDisplayName(tutor)}
        description="This screen combines GET /api/tutors/:tutorId/ and GET /api/tutors/:tutorId/availability/ on one page."
      />

      <div className="teacher-grid teacher-grid-two">
        <TeacherCard
          title="Tutor Profile"
          description="Public tutor information returned by the backend."
          actions={<Link className="teacher-button-secondary" to="/teachers">Back to teachers</Link>}
        >
          {tutorDetailQuery.isLoading ? (
            <TeacherLoadingState label="Loading tutor detail..." />
          ) : tutorNotFound ? (
            <TeacherEmptyState
              title="Tutor not found"
              description="The backend returned a 404 for this tutor id."
            />
          ) : tutorDetailQuery.isError ? (
            <TeacherEmptyState
              title="Unable to load tutor"
              description={tutorDetailQuery.error.message}
            />
          ) : (
            <TeacherKeyValueGrid entries={tutorEntries} />
          )}
        </TeacherCard>

        <TeacherCard
          title="Availability"
          description="Tutor availability returned by the backend."
        >
          {tutorDetailQuery.isLoading || tutorAvailabilityQuery.isLoading ? (
            <TeacherLoadingState label="Loading tutor availability..." />
          ) : tutorNotFound ? (
            <TeacherEmptyState
              title="Tutor unavailable"
              description="Availability cannot be loaded because the tutor detail request returned 404."
            />
          ) : tutorAvailabilityQuery.isError ? (
            <TeacherEmptyState
              title="Unable to load availability"
              description={tutorAvailabilityQuery.error.message}
            />
          ) : !availability.length ? (
            <TeacherEmptyState
              title="No available slots"
              description="The backend returned no availability for this tutor."
            />
          ) : (
            <div className="teacher-list">
              {availability.map((slot, index) => (
                <div key={`${renderAvailabilityLabel(slot)}-${index}`} className="teacher-list-card">
                  <div className="teacher-list-card-main">
                    <h3 style={{ margin: 0 }}>Available Slot</h3>
                    <p style={{ margin: "0.25rem 0 0" }}>{renderAvailabilityLabel(slot)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TeacherCard>
      </div>
    </TeacherPageShell>
  );
}
